// @ts-nocheck
import React from "react";
import PrecipPercentile from "../../forecastGraphs/PrecipPercentile";
import ScatterGraph from "../../forecastGraphs/ScatterGraph";
import ProcessedScatterGraph from "../../forecastGraphs/ProcessedScatterGraph";
import ComposedForecastCombinedChart from "../../ComposedForecast/ComposedForecastCombinedChart";
import {
  formatProcessedDataGraph,
  separatePercentileChannels,
} from "../../../util/utils";

//This component renders different asset graph displays based on the active forecast and variation selected.
// It supports various forecast types including Precipitation, Wind, Solar, Temperature, Humidity, Soil Moisture, Snow Accumulation, and processed model data.
const AssetGraphDisplay = ({
  activeForecast,
  forecastVariation,
  timeZone,
  assetTimeZone,
  interpolateChecked,
  precipitation,
  wind10,
  wind80,
  windPower80Data,
  solarRadiation,
  solarPowerData,
  temperature2m,
  humidity2m,
  soilMoisture,
  snowAcc,
  highResPrecip,
  highResSolar,
  highResWind10,
  highResWind80,
  highResWindPower80Data,
  highResTemp,
  highResHumidity,
  graphCastPrecip,
  graphCastWind,
  graphCastTemp,
  processedModelData,
  composedForecastData,
  additionalDataSources,
  foreCastSchema,
  onTimeZoneChange,
  onRegisterControls,
  renderLoading,
  desktopExtraControls,
  hideGraphControls,
}) => {
  // Render loading state if no active forecast is selected
  if (!activeForecast) {
    return renderLoading();
  }

  // Render different graph components based on the active forecast type
  // Each forecast type has its own data structure and rendering logic

  // When Precipitation forecast is selected
  if (activeForecast === "Precipitation") {
    if (!precipitation) {
      return renderLoading();
    }

    let format = interpolateChecked ? "interpolated" : "raw";
    const data = precipitation[format][forecastVariation];

    if (!data) {
      return renderLoading();
    }

    let highResData = highResPrecip?.Hourly;
    if (forecastVariation === "Cumulative") {
      highResData = highResPrecip?.Cumulative;
    }

    let graphCastData = null;
    if (graphCastPrecip) {
      try {
        if (
          forecastVariation === "Cumulative" &&
          graphCastPrecip.interpolated?.Cumulative?.control
        ) {
          graphCastData = {
            data: graphCastPrecip.interpolated.Cumulative.control,
            color: "#FF8C00",
          };
        } else if (graphCastPrecip.interpolated?.Hourly?.control) {
          graphCastData = {
            data: graphCastPrecip.interpolated.Hourly.control,
            color: "#FF8C00",
          };
        }
      } catch (error) {
        console.error("Error processing GraphCast precipitation data:", error);
        graphCastData = null;
      }
    }

    return (
      <PrecipPercentile
        timeZone={timeZone}
        assetTimeZone={assetTimeZone}
        highResData={highResData}
        data={data.percentiles}
        average={data?.average}
        control={data?.control}
        graphCastData={graphCastData}
        yaxis="Precipitation (mm)"
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When Wind10 forecast is selected
  if (activeForecast === "Wind10") {
    if (!wind10) {
      return renderLoading();
    }

    let format = interpolateChecked ? "interpolated" : "raw";
    const data = wind10[format][forecastVariation];

    let yaxis = "Speed (m/s)";
    let highResData = highResWind10?.Speed;

    if (forecastVariation === "Direction") {
      yaxis = "Direction (degrees)";
      highResData = highResWind10?.Direction;
    }

    let graphCastData = null;
    if (graphCastWind) {
      try {
        if (
          forecastVariation === "Direction" &&
          graphCastWind.interpolated?.Direction
        ) {
          graphCastData = {
            data: graphCastWind.interpolated.Direction,
            color: "#FF8C00",
          };
        } else if (graphCastWind.interpolated?.Speed) {
          graphCastData = {
            data: graphCastWind.interpolated.Speed,
            color: "#FF8C00",
          };
        }
      } catch (error) {
        console.error("Error processing GraphCast wind data:", error);
        graphCastData = null;
      }
    }

    return (
      <ScatterGraph
        timeZone={timeZone}
        assetTimeZone={assetTimeZone}
        data={data.percentiles}
        highResData={highResData}
        average={data?.average}
        control={data?.control}
        graphCastData={graphCastData}
        yaxis={yaxis}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When Wind80 forecast is selected
  if (activeForecast === "Wind80") {
    if (!wind80) {
      return renderLoading();
    }

    let format = interpolateChecked ? "interpolated" : "raw";

    const dataSource = forecastVariation === "Power" ? windPower80Data : wind80;
    const data = dataSource?.[format]?.[forecastVariation];
    if (!data) {
      return renderLoading();
    }

    let yaxis = null;
    let highResData = null;

    switch (forecastVariation) {
      case "Direction":
        yaxis = "Direction (degrees)";
        highResData = highResWind80?.Direction;
        break;
      case "Speed":
        yaxis = "Speed (m/s)";
        highResData = highResWind80?.Speed;
        break;
      case "Power":
        yaxis = "MW";
        highResData = highResWindPower80Data;
        break;
      default:
        break;
    }

    let graphCastData = null;
    
    return (
      <ScatterGraph
        timeZone={timeZone}
        data={data.percentiles}
        highResData={highResData}
        average={data?.average}
        control={data?.control}
        graphCastData={graphCastData}
        yaxis={yaxis}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
        assetTimeZone={assetTimeZone}
      />
    );
  }
  // When Solar forecast is selected
  if (activeForecast === "Solar") {
    if (!solarRadiation) {
      return renderLoading();
    }
    let format = interpolateChecked ? "interpolated" : "raw";
    let yaxis = null;
    let highResData = null;
    let data = null;
    let lineTension = interpolateChecked ? 0.0 : 0.2;

    switch (forecastVariation) {
      case "lWave":
        yaxis = "Rad. Flux (W/m²)";
        data = solarRadiation?.[format]?.["lWave"];
        highResData = highResSolar?.solarL;
        break;
      case "sWave":
        data = solarRadiation?.[format]?.["sWave"];
        yaxis = "Rad. Flux (W/m²)";
        highResData = highResSolar?.solarS;
        break;
      case "Power":
        data = solarPowerData?.[format]?.["Power"];
        yaxis = "MW";
        highResData = solarPowerData?.ohhfc;
        break;
      default:
        break;
    }

    if (!data) {
      return renderLoading();
    }

    return (
      <ScatterGraph
        timeZone={timeZone}
        data={data.percentiles}
        highResData={highResData}
        control={data.control}
        average={data.average}
        yaxis={yaxis}
        lineTension={lineTension}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When Temperature forecast is selected
  if (activeForecast === "Temperature") {
    let format = interpolateChecked ? "interpolated" : "raw";
    let yaxis = null;
    let highResData = null;
    let data = null;
    let lineTension = interpolateChecked ? 0.0 : 0.2;

    let graphCastData = null;
    if (graphCastTemp) {
      try {
        if (graphCastTemp.interpolated?.Hourly?.control) {
          graphCastData = {
            data: graphCastTemp.interpolated.Hourly.control,
            color: "#FF8C00",
          };
        }
      } catch (error) {
        console.error("Error processing GraphCast temperature data:", error);
        graphCastData = null;
      }
    }

    switch (forecastVariation) {
      case "Hourly":
        yaxis = "Temperature °C";
        data = temperature2m?.[format]?.["Hourly"];
        highResData = highResTemp;
        break;
      default:
        break;
    }

    if (!data) {
      return renderLoading();
    }

    return (
      <ScatterGraph
        timeZone={timeZone}
        data={data.percentiles}
        highResData={highResData}
        control={data.control}
        average={data.average}
        graphCastData={graphCastData}
        yaxis={yaxis}
        lineTension={lineTension}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When Humidity forecast is selected
  if (activeForecast === "Humidity") {
    let format = interpolateChecked ? "interpolated" : "raw";
    let yaxis = null;
    let highResData = null;
    let data = null;
    let lineTension = interpolateChecked ? 0.0 : 0.2;

    switch (forecastVariation) {
      case "Hourly":
        yaxis = "Relative Humidity %";
        data = humidity2m?.[format]?.["Hourly"];
        highResData = highResHumidity;
        break;
      default:
        break;
    }

    if (!data) {
      return renderLoading();
    }

    return (
      <ScatterGraph
        timeZone={timeZone}
        data={data.percentiles}
        highResData={highResData}
        control={data.control}
        average={data.average}
        yaxis={yaxis}
        lineTension={lineTension}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When Soil Moisture forecast is selected
  if (activeForecast === "SoilMoisture") {
    let format = interpolateChecked ? "interpolated" : "raw";
    let yaxis = null;
    let highResData = null;
    let data = null;
    let lineTension = interpolateChecked ? 0.0 : 0.2;

    switch (forecastVariation) {
      case "Hourly":
        yaxis = "Soil Moisture (%)";
        data = soilMoisture?.[format]?.["Hourly"];
        break;
      default:
        break;
    }

    if (!data) {
      return renderLoading();
    }

    return (
      <ScatterGraph
        timeZone={timeZone}
        data={data.percentiles}
        highResData={highResData}
        control={data.control}
        average={data.average}
        yaxis={yaxis}
        lineTension={lineTension}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When Snow Moisture forecast is selected
  if (activeForecast === "SnowAcc") {
    let format = interpolateChecked ? "interpolated" : "raw";
    let yaxis = null;
    let highResData = null;
    let data = null;
    let lineTension = interpolateChecked ? 0.0 : 0.2;

    switch (forecastVariation) {
      case "Hourly":
        yaxis = "Snow Water Equivalent (mm)";
        data = snowAcc?.[format]?.["Hourly"];
        highResData = null;
        break;
      default:
        break;
    }

    if (!data) {
      return renderLoading();
    }

    return (
      <PrecipPercentile
        timeZone={timeZone}
        data={data.percentiles}
        highResData={highResData}
        control={data.control}
        average={data.average}
        yaxis="Snow Water Equivalent (mm)"
        lineTension={lineTension}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }
  // When processed model data forecast is selected
  if (activeForecast.includes("processed-")) {
    const processedActiveForecast = activeForecast.split("-")[1];

    let graphCastData = null;
    if (!processedModelData || !processedModelData[processedActiveForecast]) {
      return renderLoading();
    }

    const processedModelRaw =
      processedModelData[processedActiveForecast].models?.[forecastVariation];

    if (!processedModelRaw || !processedModelRaw.data) {
      return renderLoading();
    }

    const processedDataRaw = processedModelRaw.data;

    const channelsFormmated = formatProcessedDataGraph(processedDataRaw);
    const { percentiles, otherChannels } =
      separatePercentileChannels(channelsFormmated);

    return (
      <ProcessedScatterGraph
        yaxis={processedModelRaw?.yUnit}
        percentiles={percentiles}
        otherChannels={otherChannels}
        timeZone={timeZone}
        assetTimeZone={assetTimeZone}
        graphCastData={graphCastData}
        title={foreCastSchema?.[activeForecast]?.name}
        onTimeZoneChange={onTimeZoneChange}
        additionalDataSources={additionalDataSources}
        onRegisterControls={onRegisterControls}
        desktopExtraControls={desktopExtraControls}
        hideGraphControls={hideGraphControls}
      />
    );
  }

  // When a composed forecast (`composed-${id}`) is selected, render the
  // dedicated ComposedForecastCombinedChart. The chart handles its own
  // time zone / controls internally (currently hardcoded UTC + hidden controls).
  if (activeForecast.startsWith("composed-")) {
    const forecastId = activeForecast.slice("composed-".length);
    const combined = composedForecastData?.[forecastId];
    if (!combined) {
      return renderLoading();
    }
    return (
      <ComposedForecastCombinedChart
        baseModel={combined.baseModel}
        overlayLines={combined.overlayLines}
        accuracy={combined.accuracy}
        fillContainer
      />
    );
  }


  return renderLoading();
};

export default AssetGraphDisplay;
